import {
  ApolloClient,
  InMemoryCache,
  split,
  ApolloLink,
  Observable,
  type Operation,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { getMainDefinition, removeDirectivesFromDocument } from '@apollo/client/utilities';
import * as AbsintheSocket from '@absinthe/socket';
import { createAbsintheSocketLink } from '@absinthe/socket-apollo-link';
import { Socket as PhoenixSocket } from 'phoenix';
import { createLink } from 'apollo-absinthe-upload-link';
import { tracingLink, injectTraceHeaders } from './tracing';
// import * as Sentry from '@sentry/react';

// Check if we're running in localhost/development mode
export function isLocalhost(): boolean {
  if (typeof window === 'undefined') return false;
  const host = window.location.host;
  return host.includes('localhost') || host.includes('127.0.0.1');
}

// Builds a return URL for auth redirects.
// In dev mode (localhost), we need the full URL because Vite runs on a different port.
// In production, we use relative paths to pass validate_redirect_url/1 security checks.
export function buildReturnUrl(path: string, hash: string): string {
  if (isLocalhost()) {
    return window.location.origin + path + hash;
  }
  return path + hash;
}

// Token refresh response from /auth/refresh endpoint
export interface TokenRefreshResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  expires_at: string | null;
}

// Callback for when tokens are refreshed
let onTokenRefresh: ((response: TokenRefreshResponse) => void) | null = null;

/**
 * Register a callback to be notified when tokens are refreshed.
 * Used by PrivateApolloProvider to update the JWT for WebSocket connections.
 */
export function setOnTokenRefresh(callback: ((response: TokenRefreshResponse) => void) | null) {
  onTokenRefresh = callback;
}

// Track if a refresh is in progress to prevent concurrent refresh calls
let isRefreshing = false;
let refreshPromise: Promise<TokenRefreshResponse | null> | null = null;

// Computes backend base URL derived from current window location
// In development (Vite dev server on port 5173), we return empty string
// to use relative URLs that go through Vite's proxy, enabling same-origin
// cookie handling for session-based auth.
export function getBackendUrl(): string {
  if (typeof window !== 'undefined') {
    const protocol = window.location.protocol;
    const host = window.location.host;

    // In development with Vite dev server, use relative URLs (empty base)
    // This allows requests to go through Vite's proxy, making them same-origin
    // which is required for SameSite=Lax cookies to be sent
    if (host.includes('localhost:5173') || host.includes('127.0.0.1:5173')) {
      return '';
    }

    // For other localhost scenarios (e.g., direct Phoenix access), use port 4000
    if (host.includes('localhost') || host.includes('127.0.0.1')) {
      return `${protocol}//${host.replace(/:\d+/, ':4000')}`;
    }

    return `${protocol}//${host}`;
  }

  // Fallback for SSR or other contexts (development)
  return 'http://localhost:4000';
}

// Computes HTTP endpoint derived from current window location
// Supports multi-endpoint routing based on endpoint name
function getHttpEndpoint(endpointName?: string): string {
  const baseUrl = getBackendUrl();

  // Map endpoint names to their API paths
  const endpointMap: Record<string, string> = {
    studio: '/api/v1alpha', // Studio operations (conversations, agents, workflows)
    asoc: '/api/asoc_v1alpha', // Asoc v1 case operations
    asoc_v2: '/api/v2alpha', // Asoc v2 case operations
  };

  // Default to studio endpoint if no endpoint specified
  const path = endpointName ? endpointMap[endpointName] || endpointMap.studio : endpointMap.studio;

  return `${baseUrl}${path}`;
}

// Helper function to extract endpoint name from GraphQL operation
function getEndpointFromOperation(operation: Operation): string | undefined {
  // Check if endpoint is already set in context
  const context = operation.getContext();
  if (context.endpoint) {
    return context.endpoint;
  }

  // Extract @endpoint directive from the operation's AST
  const definition = operation.query.definitions.find(
    (def: any): def is any => def.kind === 'OperationDefinition'
  );

  if (!definition || !definition.directives) {
    return undefined;
  }

  const endpointDirective = definition.directives.find((dir: any) => dir.name.value === 'endpoint');

  if (!endpointDirective) {
    return undefined;
  }

  const nameArg = endpointDirective.arguments?.find((arg: any) => arg.name.value === 'name');

  return nameArg?.value?.value;
}

// Link that strips @endpoint directive from queries before sending to server
// The @endpoint directive is client-side only for routing purposes
const removeEndpointDirectiveLink = new ApolloLink((operation: Operation, forward: any) => {
  // Extract endpoint name before removing the directive
  const endpointName = getEndpointFromOperation(operation);

  // Remove @endpoint directive from the query
  const cleanedQuery = removeDirectivesFromDocument(
    [{ name: 'endpoint', remove: true }],
    operation.query
  );

  if (cleanedQuery) {
    operation.query = cleanedQuery;
  }

  // Store endpoint name in context for routing
  operation.setContext({
    ...operation.getContext(),
    endpoint: endpointName,
  });

  return forward(operation);
});

// Link that adds credentials to all HTTP requests for session-based auth
const credentialsLink = setContext((_, { headers }) => ({
  headers,
  credentials: 'include', // Send cookies with requests for session-based auth
}));

// Returns a new Absinthe upload link instance with multi-endpoint routing
// This supports file uploads to Absinthe GraphQL backends and routes based on @endpoint directive
const makeHttpLink = () => {
  // Create separate links for each endpoint
  const studioLink = createLink({ uri: getHttpEndpoint('studio') });
  const asocLink = createLink({ uri: getHttpEndpoint('asoc') });
  const asocV2Link = createLink({ uri: getHttpEndpoint('asoc_v2') });

  // Route based on endpoint stored in context (after @endpoint directive is stripped)
  const routingLink = ApolloLink.split(
    (operation: Operation) => operation.getContext().endpoint === 'asoc_v2',
    asocV2Link,
    ApolloLink.split(
      (operation: Operation) => operation.getContext().endpoint === 'asoc',
      asocLink,
      studioLink // default to studio endpoint
    )
  );

  // Chain: add credentials → strip @endpoint directive → route to correct endpoint
  return ApolloLink.from([credentialsLink, removeEndpointDirectiveLink, routingLink]);
};

// Returns a new Apollo Client instance which uses an HTTP link for all operations
export const makePublicClient = () => {
  return new ApolloClient({
    link: makeHttpLink(),
    cache: new InMemoryCache({
      typePolicies: {
        NodeExecution: {
          keyFields: ['nodeId'],
        },
      },
    }),
  });
};

export const publicClient = makePublicClient();

/**
 * Attempts to silently refresh the session via /auth/refresh.
 * Returns the token response on success, or null on failure.
 * Uses a singleton pattern to prevent concurrent refresh calls.
 */
async function attemptSilentRefresh(): Promise<TokenRefreshResponse | null> {
  // If already refreshing, wait for that to complete
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const backendUrl = getBackendUrl();
      const response = await fetch(`${backendUrl}/auth/refresh`, {
        method: 'POST',
        credentials: 'include', // Send cookies
        headers: {
          Accept: 'application/json',
        },
      });

      if (response.ok) {
        const data: TokenRefreshResponse = await response.json();
        console.log('🔄 Session refreshed successfully');

        // Notify any registered callbacks (e.g., PrivateApolloProvider)
        if (onTokenRefresh) {
          onTokenRefresh(data);
        }

        return data;
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.log('🔐 Session refresh failed:', errorData.error || response.status);
        return null;
      }
    } catch (error) {
      console.log('🔐 Session refresh error:', error);
      return null;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/**
 * Redirects to the login page with the current URL as the return destination.
 * Uses buildReturnUrl to handle localhost vs production URL formatting.
 */
function redirectToLogin() {
  const returnUrl = buildReturnUrl(window.location.pathname, window.location.hash);
  const backendUrl = getBackendUrl();
  const loginUrl = `${backendUrl}/auth/login?redirect=${encodeURIComponent(returnUrl)}`;
  console.log('🔐 Redirecting to login...');
  window.location.href = loginUrl;
}

/**
 * Checks if an error indicates an authentication problem.
 */
function isAuthError(networkError: any, graphQLErrors: readonly any[] | undefined): boolean {
  // Check for 401 in network errors
  if (networkError && 'statusCode' in networkError && networkError.statusCode === 401) {
    return true;
  }

  // Check for UNAUTHENTICATED errors in GraphQL responses
  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      const message = err.message?.toLowerCase() || '';
      if (
        err.extensions?.code === 'UNAUTHENTICATED' ||
        message.includes('unauthenticated') ||
        message.includes('unauthorized') ||
        message.includes('not authenticated')
      ) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Error link that handles authentication errors with retry logic.
 *
 * When a 401 or UNAUTHENTICATED error is received:
 * 1. Attempt to silently refresh the session via /auth/refresh
 * 2. If refresh succeeds, retry the original request
 * 3. If refresh fails (session truly expired), redirect to login
 *
 * This prevents the "redirect to home" problem during token refresh.
 */
const authErrorLink = onError(({ networkError, graphQLErrors, operation, forward }) => {
  if (!isAuthError(networkError, graphQLErrors)) {
    return; // Not an auth error, let it pass through
  }

  console.log('🔐 Auth error detected, attempting silent refresh...');

  // Return an Observable that handles the retry logic
  return new Observable((observer) => {
    attemptSilentRefresh()
      .then((refreshResult) => {
        if (refreshResult) {
          // Refresh succeeded - retry the original operation
          console.log('🔄 Retrying request after session refresh...');
          forward(operation).subscribe({
            next: observer.next.bind(observer),
            error: observer.error.bind(observer),
            complete: observer.complete.bind(observer),
          });
        } else {
          // Refresh failed - session is truly expired, redirect to login
          redirectToLogin();
          observer.complete();
        }
      })
      .catch((error) => {
        console.error('🔐 Unexpected error during refresh:', error);
        redirectToLogin();
        observer.complete();
      });
  });
});

// Returns a new ApolloLink instance which combines error handling, tracing,
// and standard HTTP link with multi-endpoint routing. Authentication is handled via session cookie.
const makeHttpLinkWithAuth = () => ApolloLink.from([authErrorLink, tracingLink, makeHttpLink()]);

// Get socket endpoint derived from current window location
// Supports multi-endpoint routing based on endpoint name
function getSocketEndpoint(endpointName?: string): string {
  // Map endpoint names to their WebSocket paths
  const endpointMap: Record<string, string> = {
    studio: '/v1alpha/graphql_socket',
    asoc: '/asoc_v1alpha/graphql_socket',
    asoc_v2: '/v2alpha/graphql_socket',
  };

  // Default to studio endpoint if no endpoint specified
  const socketPath = endpointName
    ? endpointMap[endpointName] || endpointMap.studio
    : endpointMap.studio;

  // In production/release, derive from current window location
  if (typeof window !== 'undefined') {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;

    // In development with Vite dev server, use localhost:4000 directly
    // (WebSocket connections work better going directly to Phoenix rather than through Vite proxy)
    if (host.includes('localhost:5173') || host.includes('127.0.0.1:5173')) {
      return `ws://localhost:4000${socketPath}`;
    }

    // For other localhost scenarios (e.g., direct Phoenix access)
    if (host.includes('localhost') || host.includes('127.0.0.1')) {
      return `ws://localhost:4000${socketPath}`;
    }

    return `${protocol}//${host}${socketPath}`;
  }

  // Fallback for SSR or other contexts (development)
  return `ws://localhost:4000${socketPath}`;
}

// Creates an Absinthe socket for WebSocket subscriptions
// Token is passed in params since cookies aren't reliably forwarded during WS upgrades
// Token refresh is handled by PrivateApolloProvider recreating the client when JWT changes
const makeAbsintheSocket = (token?: string, endpointName?: string) => {
  if (typeof window === 'undefined') {
    return null;
  }

  const endpoint = getSocketEndpoint(endpointName);

  // Get trace context headers for WebSocket connection
  const traceHeaders = (() => {
    try {
      const headers = injectTraceHeaders({});

      // Return only the trace headers we need for WebSocket params
      const params: Record<string, string> = {};
      if (headers.traceparent) params.traceparent = headers.traceparent;
      if (headers.tracestate) params.tracestate = headers.tracestate;

      return params;
    } catch (e) {
      return {};
    }
  })();

  // V2 Alpha requires Bearer token in Authorization param
  // V1 Alpha uses 'token' param
  const authParams = token
    ? endpointName === 'asoc_v2'
      ? { Authorization: `Bearer ${token}` }
      : { token }
    : {};

  const phoenixSocket = new PhoenixSocket(endpoint, {
    params: {
      ...traceHeaders,
      ...authParams,
    },
  });

  // Connect immediately
  phoenixSocket.connect();

  return AbsintheSocket.create(phoenixSocket);
};

const makeAbsintheSocketLink = (token?: string, endpointName?: string) => {
  const socket = makeAbsintheSocket(token, endpointName);
  return socket ? (createAbsintheSocketLink(socket) as any) : undefined;
};

const makeSplitLink = (token?: string, endpointName?: string) => {
  const socketLink = makeAbsintheSocketLink(token, endpointName);
  const httpLink = makeHttpLinkWithAuth();

  // If we can't create a socket link, just use HTTP for everything
  if (!socketLink) {
    return httpLink;
  }

  return split(
    ({ query }: any) => {
      const definition = getMainDefinition(query);
      const isSubscription =
        definition.kind === 'OperationDefinition' && definition.operation === 'subscription';

      // Always use WebSocket for subscriptions - they require real-time connection
      return isSubscription;
    },
    socketLink,
    httpLink
  );
};

// Returns a new Apollo Client instance which uses a split link, meaning an HTTP link for
// queries and mutations, and an absinthe socket link for subscriptions.
// HTTP auth is via session cookie. WebSocket auth passes token in params.
// This client uses the Studio (V1alpha) WebSocket endpoint for subscriptions.
export const makePrivateClient = (token?: string) =>
  new ApolloClient({
    link: makeSplitLink(token, 'studio'),
    cache: new InMemoryCache({
      typePolicies: {
        NodeExecution: {
          keyFields: ['nodeId'],
        },
      },
    }),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'cache-and-network',
      },
    },
  });

// Returns a new Apollo Client instance specifically for V2 Alpha subscriptions.
// Uses the V2alpha WebSocket endpoint (/v2alpha/graphql_socket).
// Required because Apollo Client can only use one WebSocket endpoint at a time.
export const makePrivateClientV2 = (token?: string) =>
  new ApolloClient({
    link: makeSplitLink(token, 'asoc_v2'),
    cache: new InMemoryCache(),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'cache-and-network',
      },
    },
  });
